import React, { useMemo } from 'react'
import { ColDef, ColGroupDef, GridOptions, ICellEditorParams } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { MarketType, ProductPermission, ProductSector } from 'api'
import { AgGridWrapper, gridOptionsBase, groupingRowColDef, TypedValueGetterParams } from 'view/components/datagrid'
import { ProductsPanelGridRow } from './ProductsPanelGridModel'
import { ProductPermissionGroupButtons } from './ProductPermissionGroupButtons'
import { ProductPermissionButtons } from './ProductPermissionButtons'
import { compareInsensitive } from 'utils'

export interface ProductsPanelGridProps {
  rowData: ProductsPanelGridRow[]
  hideWritePermission?: boolean
  hideViewPermission?: boolean
  disabled?: boolean
  onUpdate: (productSectorIds: string[], permission: ProductPermission) => void
  marketType: MarketType
}

const noProductsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_PRODUCTS_AVAILABLE)}</div>

const cellRenderer = React.forwardRef((params: ICellEditorParams, ref: React.Ref<any>) => {
  const allLeafChildren = params.node.allLeafChildren ?? []

  return params.node.group ? (
    <ProductPermissionGroupButtons
      ref={ref}
      rows={allLeafChildren.map(node => node.data)}
      hideWritePermission={params.context.hideWritePermission}
      hideViewPermission={params.context.hideViewPermission}
      disabled={params.context.disabled}
      onUpdatePermissions={params.context.onUpdatePermissions}
    />
  ) : (
    <ProductPermissionButtons
      ref={ref}
      row={params.node.data}
      hideWritePermission={params.context.hideWritePermission}
      hideViewPermission={params.context.hideViewPermission}
      disabled={params.context.disabled}
      onUpdatePermissions={params.context.onUpdatePermissions}
    />
  )
})

const getColumnDefs = ({
  hideViewPermission,
  hideWritePermission,
  marketType = MarketType.PRIMARY
}: {
  hideViewPermission?: boolean
  hideWritePermission?: boolean
  marketType?: MarketType
}): (ColDef | ColGroupDef)[] => [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_GROUPING),
    width: 180,
    minWidth: 130,
    headerComponent: undefined,
    pinned: false,
    hide: false,
    resizable: true
  },
  {
    colId: 'MARKET',
    valueGetter: (params: TypedValueGetterParams<ProductsPanelGridRow>) =>
      marketType === MarketType.PRIMARY ? params.data?.product.market : params?.data?.product?.secondaryMarket,
    rowGroup: true,
    hide: true
  },
  {
    colId: 'PRODUCT_NAME',
    valueGetter: (params: TypedValueGetterParams<ProductsPanelGridRow>) => params.data?.product.name,
    rowGroup: true,
    hide: true
  },
  {
    colId: 'PRODUCT_SECTOR_NAME',
    valueGetter: (params: TypedValueGetterParams<ProductsPanelGridRow>) => params.data?.productSector.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    suppressMovable: true,
    resizable: true,
    width: 200,
    minWidth: 200
  },
  {
    colId: 'PERMISSION',
    valueGetter: (params: TypedValueGetterParams<ProductsPanelGridRow>) => params.data?.permission,
    headerName: Locale.translate(Translation.TC_PERMISSIONS),
    width: hideWritePermission || hideViewPermission ? 100 : 150,
    suppressMovable: true,
    resizable: true,
    sortable: false,
    aggFunc: () => NaN,
    cellRenderer
  }
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  initialGroupOrderComparator: ({ nodeA, nodeB }) => compareInsensitive(nodeA.key ?? '', nodeB.key ?? ''),
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.productSector.id
}

export const ProductsPanelGrid: React.FC<ProductsPanelGridProps> = ({
  marketType,
  rowData,
  hideWritePermission,
  hideViewPermission,
  disabled,
  onUpdate
}) => {
  const onUpdatePermissions = (productSectors: ProductSector[], permission: ProductPermission) => {
    onUpdate(
      productSectors.map(productSector => productSector.id),
      permission
    )
  }

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        hideViewPermission,
        hideWritePermission,
        marketType
      }),
    [hideViewPermission, hideWritePermission, marketType]
  )

  return (
    <AgGridWrapper
      className="datagrid-borderless"
      gridOptions={gridOptions}
      columnDefs={columnDefs}
      rowData={rowData}
      context={{
        onUpdatePermissions,
        hideWritePermission,
        hideViewPermission,
        disabled
      }}
      placeholder={noProductsPlaceholder}
    />
  )
}
